import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

/** Die beiden Listen, die dieser Abschnitt pflegt (A40). */
export type WegArt = 'bestellwege' | 'beschaffungswege';

/** Ein Weg, wie ihn die Einstellungen zeigen. */
export type WegVerwaltung = {
  id: number;
  /** Der Schlüssel. Steht im Programm und ist deshalb unveränderlich. */
  code: string;
  label: string;
  /** Nur Bestellwege tragen ein Sinnbild; bei Beschaffungswegen null. */
  icon: string | null;
  aktiv: boolean;
  sort_order: number;
  /** Wie viele Vorgänge daran hängen — die Antwort auf „warum kein Löschen". */
  verwendungen: number;
};

/**
 * Die Liste einer Art, MIT den stillgelegten Einträgen.
 *
 * Nicht `/lookups.php`: der filtert `aktiv = 1`, weil er dem Erfassen
 * dient. Wer pflegt, muss gerade die stillgelegten sehen — sonst kann
 * er sie nie wieder einschalten.
 */
export function useWege(art: WegArt) {
  return useQuery({
    queryKey: ['wege-verwaltung', art],
    queryFn: () => apiFetch<{ art: WegArt; wege: WegVerwaltung[] }>(`/wege/liste.php?art=${art}`),
    staleTime: 60_000
  });
}

export type WegPatch = {
  id: number;
  label?: string;
  icon?: string | null;
  aktiv?: boolean;
  sort_order?: number;
};

export type WegNeu = {
  code: string;
  label: string;
  icon?: string | null;
  sort_order?: number;
};

/**
 * Nach jedem Schreiben werden ALLE Listen ungültig, die diese Wege
 * zeigen: die Pflege selbst und die Auswahl beim Erfassen
 * (`lookups`). Wer nur die erste erneuert, legt einen Weg still und
 * bietet ihn im Erfassungs-Formular weiter an, bis jemand neu lädt.
 */
function useWegeInvalidieren(art: WegArt) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['wege-verwaltung', art] });
    void qc.invalidateQueries({ queryKey: ['lookups'] });
  };
}

export function useWegAendern(art: WegArt) {
  const erneuern = useWegeInvalidieren(art);
  return useMutation({
    mutationFn: (patch: WegPatch) =>
      apiFetch<{ geaendert: string[]; weg: WegVerwaltung }>('/wege/aendern.php', {
        method: 'PATCH',
        body: JSON.stringify({ art, ...patch })
      }),
    onSuccess: erneuern
  });
}

export function useWegAnlegen(art: WegArt) {
  const erneuern = useWegeInvalidieren(art);
  return useMutation({
    mutationFn: (neu: WegNeu) =>
      apiFetch<{ weg: WegVerwaltung }>('/wege/anlegen.php', {
        method: 'POST',
        body: JSON.stringify({ art, ...neu })
      }),
    onSuccess: erneuern
  });
}
